import type { Locale, TranslationKey } from '../i18n.js';
import type { SessionResponse } from '../lib/api.js';
import type { ThemeMode } from '../lib/theme.js';
import { ScopeBadge, UserMenu } from './UserMenu.js';

type Props = {
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  session: SessionResponse | null;
  locale: Locale;
  mode: ThemeMode;
  version: string | null;
  onLocale: (locale: Locale) => void;
  onMode: (mode: ThemeMode) => void;
  onPersona: (persona: string) => void;
  onHelp: () => void;
  onLogout: () => void;
};

/**
 * Barre supérieure.
 *
 * Les actions globales sont à **position fixe** (Charte §6) : langue, thème et déconnexion
 * vivent toujours au même endroit, derrière le bouton rond de droite — comme dans ODE.
 * La barre elle-même ne porte que ce qui doit se lire sans cliquer : le nom du produit,
 * l'identité, et le **badge de périmètre**.
 */
export function Header({
  t,
  session,
  locale,
  mode,
  version,
  onLocale,
  onMode,
  onPersona,
  onHelp,
  onLogout,
}: Props) {
  return (
    <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2">
        <div className="mr-auto flex items-baseline gap-2">
          <span className="text-lg font-bold text-accent">{t('app.name')}</span>
          <span className="hidden text-sm text-muted sm:inline">{t('app.tagline')}</span>
        </div>

        {session?.user && (
          <div className="flex items-center gap-2">
            {/* Le nom passe à la trappe sur petit écran ; le badge, jamais : c'est lui qui
                explique ce qu'on voit. */}
            <span className="hidden max-w-[16rem] truncate text-sm sm:inline">
              {session.user.name}
            </span>
            <ScopeBadge t={t} session={session} />
          </div>
        )}

        {version && (
          <span
            className="hidden text-xs text-muted md:inline"
            title={t('footer.version', { version })}
          >
            {version}
          </span>
        )}

        {session && (
          <UserMenu
            t={t}
            session={session}
            locale={locale}
            mode={mode}
            onLocale={onLocale}
            onMode={onMode}
            onPersona={onPersona}
            onHelp={onHelp}
            onLogout={onLogout}
          />
        )}
      </div>

      {session?.stubAuth && (
        <p className="bg-warn/15 px-4 py-1.5 text-center text-xs text-fg">
          {/* Doublé d'un mot, jamais porté par la seule couleur (Charte §7). */}
          <span aria-hidden="true">⚠ </span>
          {t('banner.stub')}
        </p>
      )}
    </header>
  );
}
